/**
 * Content Configuration
 * 
 * This file replaces Directus CMS content with hardcoded values.
 * Update these values as needed instead of managing content in Directus.
 */

// ============================================
// SITE METADATA & LAYOUT
// ============================================

export const siteMetadata = {
  title: 'Vitl',
  titleTemplate: '%s · Vitl',
  description: 'Personalised vitamins',
  siteUrl: process.env.GATSBY_HOST_NAME,
};

export const layoutConfig = {
  logo: {
    title: 'Vitl Logo',
    filename_disk: '0f304426-9c79-4be8-809c-2e71505c26e2.svg',
  },
  logo_primary: {
    title: 'Vitl Primary Logo',
    filename_disk: '0f304426-9c79-4be8-809c-2e71505c26e2.svg',  // Same as logo
  },
  navigation_links: [] as { label: string; path: string }[],
  navigation_links_2: [] as { label: string; path: string }[],
  social_links_label: 'Follow us',
  social_links: [
    { icon: 'instagram', url: 'https://www.instagram.com/vitllife/' },
    { icon: 'facebook', url: 'https://www.facebook.com/VitlLife/' },
    { icon: 'twitter', url: 'https://twitter.com/vitllife' },
  ],
  sign_up_label: 'Sign up to our newsletter',
  sign_up_placeholder: 'Enter your email',
  cookies_description: 'We use cookies to improve your experience on our site.',
  cookies_button_label: 'Accept',
  read_label_text: 'Read more',
};

export const footerPages: { path: string; title: string }[] = [];

export const modalConfig = {
  // UTM modals configuration
  modals: [
    {
      type: 'special-offer',
      redirect_path: '/lp/3-50',
      image: {
        filename_disk: 'modal-special-offer.jpg',
      },
    },
  ],
};

// ============================================
// FEATURE FLAGS
// ============================================

export const featureFlags = {
  showNewResultsFlow: true,
  enableChatSupport: true,
  showRecommendations: true,
  // Add more feature flags as needed
};

// ============================================
// ACCOUNT / RESULTS PAGES CONTENT
// ============================================

export const accountContent = {
  page_title: 'My Account',
  page_description: 'Manage your Vitl account and view your health recommendations',
  
  // Sign in page
  signin_title: 'Sign in to your account',
  signin_email_label: 'Email',
  signin_signin_button_label: 'Sign in',
  signin_guest_button_label: 'Continue as guest',
  signin_register_button_label: 'Create account',
  signin_no_account: "Don't have an account?",
  
  // Recommendations
  recommendation_unavailable_title: 'Recommendations Unavailable',
  recommendation_unavailable_text: 'Please complete the health consultation to get personalized recommendations.',
  recommendation_unavailable_close_button_label: 'Close',
  
  // Goals management
  goals_manage_label: 'Manage your health goals',
  goals_manage_button_label: 'Update goals',
  
  // Email preferences
  email_preferences_title: 'Email Preferences',
  email_preferences_label: 'Receive health tips and offers',
  communication_preferences_text: 'Manage your communication preferences',
  communication_email_label: 'Email',
  communication_sms_label: 'SMS',
  
  // Reviews
  reviews_title: 'What our customers say',
  reviews_items: [
    {
      reviewerName: 'Sarah M.',
      review: 'The personalized vitamins have made such a difference to my energy levels!',
    },
    {
      reviewerName: 'James P.',
      review: 'Finally, a vitamin service that actually understands my needs.',
    },
  ],
};

export const consultationResultContent = {
  section_1_title: 'Your Personalized Plan',
  section_1_subtitle: 'Based on your health consultation',
  
  section_3_title: 'Science-backed nutrition',
  section_3_description: 'Every formula is created by nutritionists and backed by science',
  section_3_footnote: '*Based on clinical studies',
  section_3_image: {
    title: 'Science backed',
    filename_disk: 'science-backed.jpg',
  },
  
  // Product images (Essential One)
  eo_bottle_image: { filename_disk: 'f48f62c6-c516-43b3-868c-766abd6c1001.jpg' },
  eo_pill_cover: { filename_disk: '7b1fda7f-1e18-40af-b841-cbf940b01834.png' },
  eo_pill_cover_full: { filename_disk: '6b5dce0e-7019-4812-a731-9bfa7dbb8bf3.png' },
  eo_main_image: { filename_disk: '35c67312-ab5c-4341-ad21-fda4fd41d94b.png' },
  eo_alt_image: { filename_disk: '7365a08e-c05d-4510-b122-511fcb68e92a.png' },
  
  // Product images (Personalized Plus / 4PP)
  pp_main_image: { filename_disk: 'f324b48e-5187-4b57-a164-cc79a2769ee6.png' },
  pp_alt_image: { filename_disk: '08553d02-afa0-48d9-bc53-3590d7394b58.png' },
  pp_alt_image_2: { filename_disk: '08553d02-afa0-48d9-bc53-3590d7394b58.png' },
  
  reviews_title: 'Customer Reviews',
  reviews_items: [
    {
      reviewerName: 'Emma L.',
      review: 'Love my personalized pack! Really convenient and effective.',
    },
  ],
  
  faqs_title: 'Frequently Asked Questions',
  faqs_subtitle: '<p>Got questions? We\'ve got answers.</p>',
  faq_categories: [
    {
      id: '1',
      name: 'Getting Started',
      questions: [
        {
          question: 'How do I get started?',
          answer: 'Complete our health consultation and we\'ll create a personalized vitamin plan for you.',
        },
        {
          question: 'How long does delivery take?',
          answer: 'We typically deliver within 3-5 business days.',
        },
      ],
    },
    {
      id: '2',
      name: 'Subscription',
      questions: [
        {
          question: 'Can I cancel my subscription?',
          answer: 'Yes, you can cancel your subscription at any time from your account settings.',
        },
        {
          question: 'How often will I receive my vitamins?',
          answer: 'Your personalized vitamins are delivered monthly.',
        },
      ],
    },
    {
      id: '3',
      name: 'Product Questions',
      questions: [
        {
          question: 'Are your vitamins vegan?',
          answer: 'Many of our formulas are vegan-friendly. Check the product details for specific information.',
        },
        {
          question: 'How should I take my vitamins?',
          answer: 'Take your daily sachet with food, preferably in the morning with breakfast.',
        },
        {
          question: 'What if I miss a day?',
          answer: 'Don\'t worry! Just continue with your regular schedule the next day. Consistency over time is what matters most.',
        },
      ],
    },
  ],
};

export const accountConsultationResultContent = {
  supplement_title: 'Your Personalized Supplements',
  supplement_list_text_1: 'Tailored to your needs',
  supplement_list_text_2: 'Science-backed formulas',
  supplement_list_text_3: 'Convenient daily packs',
  supplement_list_text_4: 'Flexible subscription',
  supplement_list_text_5: 'Cancel anytime',
  
  product_description_list_1: 'Personalized to your health goals',
  product_description_list_2: 'Delivered monthly to your door',
  product_description_list_3: 'Backed by nutritionists',
  product_description_list_4: 'Made in the UK',
  
  button_text: 'Get Started',
  
  product_image: { filename_disk: 'consultation-result-product.png' },
  teo_banner: { filename_disk: 'teo-banner.jpg' },
  
  no_consultation_title: 'No Consultation Found',
  no_consultation_description: 'Please complete a health consultation to see your results.',
  no_consultation_button_label: 'Start Consultation',
};

export const accountGoalContent = {
  manage_title: 'Manage Your Goals',
  manage_no_goals_label: 'No goals selected yet',
  manage_save_button_label: 'Save',
  manage_cancel_button_label: 'Cancel',
  read_label_text: 'Read more',
};

export const pillComponentContent = {
  title: 'What\'s Inside',
  description: 'Each pill is carefully formulated with premium ingredients',
  pill_image: { filename_disk: 'pill-visual.png' },
  formulation_title: 'Premium Formulation',
  formulation_description: 'Made with bioavailable nutrients for maximum absorption',
  directions_description: 'Take one sachet daily with food',
};

export const benefitsComponentContent = {
  title: 'The Vitl Difference',
  subtitle: 'Why thousands trust Vitl',
  items: [
    { name: 'Personalized', image_hash: 'personalized-icon' },
    { name: 'Science-backed', image_hash: 'science-icon' },
    { name: 'Convenient', image_hash: 'convenient-icon' },
    { name: 'Flexible', image_hash: 'flexible-icon' },
  ],
};

// ============================================
// LANDING PAGES CONTENT
// ============================================

export const facebookLandingContent = {
  page_title: 'Feel your best with personalised supplements | Vitl',
  page_description: 'Take our free consultation and get personalized vitamins delivered to your door.',
  
  hero_title: 'Your future is personal',
  hero_description: 'Science-backed nutrition tailored to you',
  hero_focus_text: 'Free trial',
  hero_focus_price: '£0',
  hero_background_image: { filename_disk: 'e644d26f-4b70-4d6c-9e93-cb827207aa3a.png' },
  hero_mobile_background_image: { filename_disk: 'e644d26f-4b70-4d6c-9e93-cb827207aa3a.png' },  // Same image for both
  hero_button_label: 'Get started',
  
  how_it_work_title: 'How it works',
  how_it_works_items: [
    {
      title: '1. Take our quiz',
      description: 'Answer questions about your health and lifestyle',
    },
    {
      title: '2. Get your plan',
      description: 'Receive personalized vitamin recommendations',
    },
    {
      title: '3. Start feeling better',
      description: 'Get your vitamins delivered monthly',
    },
  ],
  
  personalized_header: 'Why personalised is your next step',
  personalized_list: [
    { text: "You're unique, so why should your vitamins be the same as anyone else's?" },
    { text: "We're not about one size fits all solutions or quick fixes because we believe in the power of making it personal." },
    { text: "Put simply, we'll give your body exactly what it needs and nothing that it doesn't." },
  ],
  personalized_button_label: 'Get Started',
  personalized_image: { filename_disk: 'b8bb1d2a-f45c-4c5c-b5c7-1ec93f2082c5.jpg' },
  
  without_bs_header: 'Nothing but goodness',
  without_bs_text: "If your body doesn't need it, we don't add it.",
  without_bs_items: [
    { filename_disk: 'quality-badge-1.svg', text: 'High quality nutrients' },
    { filename_disk: 'quality-badge-2.svg', text: 'Non-GMO' },
    { filename_disk: 'quality-badge-3.svg', text: 'Ultra absorbable ingredients' },
    { filename_disk: 'quality-badge-4.svg', text: 'Vegan friendly' },
    { filename_disk: 'quality-badge-5.svg', text: 'Scientifically-backed formula' },
  ],
  
  partners_header: 'AS SEEN IN',
  
  range_header: 'Shop the range',
  shop_the_range_items: [
    { range_title: 'Personalized Plus', filename_disk: 'product-4pp.jpg', path: '/consultation' },
    { range_title: 'Essential One', filename_disk: 'product-eo.jpg', path: '/consultation' },
  ],
  
  review_title: 'What our customers are saying',
  reviews_items: [
    { review: 'Within 2 weeks of taking the vitamins, I noticed a big difference in my mental and physical health and wellbeing. Thank you for providing such an efficient product.', rating: 5, reviewerName: 'Hana' },
    { review: 'Simply, it worked for me. Felt more energized and mentally positive during the whole day. Very happy with the products.', rating: 5, reviewerName: 'Marcin' },
    { review: "Love the personalisation, the purity & quality of the products, love the packaging and have had great, easy interactions with your service teams.", rating: 5, reviewerName: 'Lyndy' },
  ],
  
  join_us_header: 'Join the community at @vitlhealth',
  join_us_text: 'Thousands are already feeling the benefits',
  join_us_image_1: { filename_disk: 'eaafb037-ece5-42f4-99fa-5f4955187c24.jpg' },
  join_us_image_2: { filename_disk: '8dad51a8-08e7-42c1-b83f-a46cb647e72b.jpg' },
  join_us_image_3: { filename_disk: '7052c3cc-39f1-417c-8bb4-6d15a08f0964.jpg' },
  join_us_image_4: { filename_disk: '5d489a15-de01-43fe-b3f1-bb4b2286fd2a.jpg' },
  join_us_text_1: 'More energy',
  join_us_text_2: 'Better sleep',
  join_us_text_3: 'Stronger immunity',
  join_us_text_4: 'Improved wellbeing',
  join_us_link_1: '/consultation',
  join_us_link_2: '/consultation',
  join_us_link_3: '/consultation',
  join_us_link_4: '/consultation',
};

export const homeExpertsContent = {
  experts: [
    {
      name: 'Christina Mamada',
      qualification: 'MSc Nutrition, BSc Biology',
      role: 'Head Nutritionist',
      image: { title: 'Christina Mamada', filename_disk: '6099dce1-f41c-4335-9aea-df3f7b925b94.png' },
    },
    {
      name: 'Dr Monika Mozere',
      qualification: 'PhD, Genetics & Bioinformatics, UCL',
      role: 'Geneticist',
      image: { title: 'Dr Monika Mozere', filename_disk: '23eb8b1e-f2fa-4b49-bbb5-44a3b9af47af.png' },
    },
    {
      name: 'Dr Monal Wadhera',
      qualification: 'BSc MBBS MRCP MRCGP',
      role: 'Medical Director',
      image: { title: 'Dr Monal Wadhera', filename_disk: '69b6a274-3aa0-4c00-8ee9-7505a2c551cf.png' },
    },
    {
      name: 'Roxane Bakker',
      qualification: 'BSc Nutrition & Dietetics',
      role: 'Head Dietitian',
      image: { title: 'Roxane Bakker', filename_disk: '0b0d3d27-951b-4b74-8086-f1f42a10117d.png' },
    },
  ],
};

export const partnersContent = {
  partners_header: 'AS SEEN IN',
  partner_logos: [
    { image_hash: '775f2236-ab46-4da1-b9e7-db434c3dbd71.svg' },
    { image_hash: '4cd461f4-1eac-4dd7-bd10-da91a083893c.svg' },
    { image_hash: '11ff206b-69b0-403d-ab9f-42942c82c11d.svg' },
    { image_hash: 'ca7b4e1e-ae7e-4c57-861c-72a359c04b51.svg' },
    { image_hash: '2dc24d0a-14ba-4683-a8cc-afafad3d2f89.svg' },
    { image_hash: '46ce32f9-548d-4466-b7b9-87453b6bfea2.svg' },
    { image_hash: '119d91e5-5e18-42d4-816e-bf80e6e07e0e.svg' },
    { image_hash: '0c8656cb-0ac3-4b5f-b449-4aa81fe88acc.svg' },
  ],
};

export const reviewsContent = {
  reviews_show: true,
  reviews_title: 'Trusted by thousands',
  reviews_items: [
    {
      reviewerName: 'Sarah M.',
      review: 'The personalized vitamins have made such a difference to my energy levels!',
    },
    {
      reviewerName: 'James P.',
      review: 'Finally, a vitamin service that actually understands my needs.',
    },
    {
      reviewerName: 'Emma L.',
      review: 'Love my personalized pack! Really convenient and effective.',
    },
  ],
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get media URL for a Directus asset
 * @param filename_disk - The filename from config
 * @returns Full URL to the media asset
 */
export const getMediaUrl = (filename_disk: string | undefined): string => {
  if (!filename_disk) return '';
  const mediaUrl = process.env.GATSBY_MEDIA_URL || '';
  return `${mediaUrl}${filename_disk}`;
};

/**
 * Get Directus Marketing media URL
 * Used for product images from the marketing CMS
 */
export const getMarketingMediaUrl = (filename_disk: string | undefined): string => {
  if (!filename_disk) return '';
  const mediaUrl = process.env.GATSBY_DIRECTUS_MARKETING_MEDIA_URL || '';
  return `${mediaUrl}/${filename_disk}`;
};
